import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, trackTimeOnPage } from '../utils/analytics';

/**
 * Custom hook for tracking page views and time on page
 */
export const useAnalytics = () => {
  const location = useLocation();
  const startTimeRef = useRef(Date.now());
  const previousPathRef = useRef('');
  const trackingTimeoutRef = useRef(null);

  useEffect(() => {
    // Clear any existing timeout
    if (trackingTimeoutRef.current) {
      clearTimeout(trackingTimeoutRef.current);
    }

    // Track time spent on previous page
    if (previousPathRef.current) {
      const timeSpent = Date.now() - startTimeRef.current;
      trackTimeOnPage(previousPathRef.current, timeSpent);
    }

    // Use a shorter delay since we now have proper visitor caching
    // The visitor info should already be initialized by AnalyticsTracker
    trackingTimeoutRef.current = setTimeout(() => {
      const currentPath = location.pathname;
      trackPageView(currentPath, document.title);
      console.log('📄 Page view tracked for:', currentPath);
    }, 500); // Reduced to 500ms for better responsiveness
    
    // Update refs
    previousPathRef.current = location.pathname;
    startTimeRef.current = Date.now();

    // Track time when user leaves the page
    const handleBeforeUnload = () => {
      const timeSpent = Date.now() - startTimeRef.current;
      trackTimeOnPage(location.pathname, timeSpent);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (trackingTimeoutRef.current) {
        clearTimeout(trackingTimeoutRef.current);
      }
    };
  }, [location.pathname]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (trackingTimeoutRef.current) {
        clearTimeout(trackingTimeoutRef.current);
      }
    };
  }, []);
};

/**
 * Hook for tracking clicks on specific elements
 */
export const useClickTracking = (elementId, elementType = 'element', additionalData = {}) => {
  const handleClick = (e) => {
    const element = e.currentTarget;
    const clickData = {
      elementType,
      elementId: elementId || element.id || '',
      elementClass: element.className || '',
      elementText: element.textContent?.trim().substring(0, 100) || '',
      href: element.href || '',
      pagePath: window.location.pathname,
      ...additionalData,
    };
    
    // Import here to avoid circular dependency
    import('../utils/analytics').then(({ trackClick }) => {
      trackClick(clickData);
    });
  };

  return handleClick;
};

